import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  ClockIcon,
  SignalIcon,
  SignalSlashIcon,
  CommandLineIcon,
  XMarkIcon, ClipboardIcon, CheckIcon, ArrowPathIcon
} from '@heroicons/react/24/solid';
import { Gauge, ChevronDown } from 'lucide-react';
import axios from 'axios';
import './theme.css';

const API_BASE = '/api';

const statusConfig = {
  enabled: {
    label: 'Enabled',
    icon: SignalIcon,
    color: 'text-[var(--status-success)]',
    badge: 'border border-[var(--status-success)] text-[var(--status-success)]',
  },
  pending: {
    label: 'Pending',
    icon: ClockIcon,
    color: 'text-[var(--status-warn)]',
    badge: 'border border-[var(--status-warn)] text-[var(--status-warn)]',
  },
  disabled: {
    label: 'Disabled',
    icon: SignalSlashIcon,
    color: 'text-[var(--text-muted)]',
    badge: 'border border-[var(--text-muted)] text-[var(--text-muted)]',
  },
};

const StatusBadge = ({ status }) => {
  const { icon: Icon, badge, color, label } = statusConfig[status] || {};
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${badge}`}>
      <Icon className={`w-4 h-4 ${color}`} />
      {label || 'Unknown'}
    </span>
  );
};

function SensorCommandPopup({ sensor, token }) {
  const [showPopup, setShowPopup] = useState(false);
  const [copied, setCopied] = useState(false);
  const [commandText, setCommandText] = useState("");
  const [expTime, setExpTime] = useState("");
  
  const handleCopy = () => {
    navigator.clipboard.writeText("python3 provision.py " + commandText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const fetchTokenAndSetCommand = async () => {
    try {
      const res = await axios.get(`${API_BASE}/admin/sensor/provisioning-token/${sensor.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const catoken = res.data.token;
      setCommandText(`${catoken}`);

      const expirationTime = new Date(Date.now() + 10 * 60 * 1000).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
      setExpTime(expirationTime);
    } catch (err) {
      console.error("Failed to fetch CA token", err);
      setCommandText("# Failed to generate provisioning command");
    }
  };

  useEffect(() => {
    // const fetchTokenAndSetCommand = async () => {
    //   try {
    //     const res = await axios.get(`${API_BASE}/admin/sensor/provisioning-token/${sensor.id}`, {
    //       headers: { Authorization: `Bearer ${token}` },
    //     });
    //     const catoken = res.data.token;
    //     setCommandText(`${catoken}`);
    //     const expirationTime = new Date(Date.now() + 10 * 60 * 1000)
    //       .toLocaleTimeString([], { 
    //         hour: '2-digit', 
    //         minute: '2-digit', 
    //         timeZoneName: 'short'  // adds timezone abbreviation like "PDT" or "GMT"
    //       });
    //     setExpTime(expirationTime);
    //   } catch (err) {
    //     console.error("Failed to fetch CA token", err);
    //     setCommandText("# Failed to generate provisioning command");
    //   }
    // };

    if (showPopup) {
      fetchTokenAndSetCommand();
    }
  }, [showPopup, sensor.id, token]);

  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (e.target.closest('.popup-content')) return;
      setShowPopup(false);
    };

    let timeoutId;
    if (showPopup) {
      // Delay attaching the click listener
      timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
    }

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showPopup]);

  return (
    
    <div className="relative inline-block">
      <button
        onClick={() => setShowPopup(true)}
        aria-label="Show provisioning command"
        title="Show provisioning command"
        className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
      >
        <CommandLineIcon className="w-5 h-5 inline" />
      </button>

      {showPopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
          <div className="relative popup-content bg-[var(--surface-900)] border border-[var(--surface-800)] shadow-2xl rounded-xl p-6 text-[var(--text-primary)] w-full max-w-2xl overflow-hidden">

            <button
              onClick={() => setShowPopup(false)}
              aria-label="Close"
              className="absolute top-2 right-2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <h2 className="text-lg font-semibold mb-2 text-left">Provisioning Command</h2>

            <p className="text-sm text-[var(--text-muted)] mb-4 text-left">
              Run the following command on your sensor device to request a device certificate and establish communication with the Signetry server. The token is valid for 10 minutes and must be refreshed if not used before <span className="text-[var(--text-primary)] font-medium">{expTime}</span>.
            </p>

            <div className="bg-[var(--ink-950)] text-[var(--text-primary)] px-4 py-3 rounded border border-[var(--surface-800)] text-sm font-[var(--font-mono)] break-all text-left">
              <span>python3 provision.py </span>
              <span className="text-[var(--signal-cyan)]">{commandText || "Loading..."}</span>
            </div>

            <div className="mt-4 flex gap-3">
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--signal-cyan)] text-[var(--signal-cyan)] hover:bg-[var(--surface-800)] transition"
                disabled={!commandText}
              >
                {copied ? (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    Copied
                  </>
                ) : (
                  <>
                    <ClipboardIcon className="w-5 h-5" />
                    Copy to Clipboard
                  </>
                )}
              </button>

              <button
                onClick={fetchTokenAndSetCommand}
                className="flex items-center gap-2 px-4 py-2 rounded-md border border-[var(--status-success)] text-[var(--status-success)] hover:bg-[var(--surface-800)] transition"
                disabled={!commandText}
              >
                <ArrowPathIcon className="w-5 h-5" />
                Refresh Token
              </button>
            </div>


          </div>
        </div>
      )}

    </div>
  );
}


export default function SensorsPage({ token = 'demo-token', role = 'admin' }) {
  const [sensors, setSensors] = useState([]);
  const [CAToken, setCAToken] = useState('');
  const [allowedSensors, setAllowedSensors] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [form, setForm] = useState({ name: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [sortKey, setSortKey] = useState('name');
  const [sortAsc, setSortAsc] = useState(true);
  const [apiMessage, setApiMessage] = useState('');
  const [apiSuccess, setApiSuccess] = useState(null);

  useEffect(() => {
    fetchSensors();
    fetchSensorLimit();
  }, []);

  useEffect(() => {
  if (apiMessage) {
    const timeout = setTimeout(() => {
      setApiMessage('');
      setApiSuccess(null);
    }, 5000);
    return () => clearTimeout(timeout);
  }
}, [apiMessage]);

  const fetchSensors = async () => {
    try {
      const res = await axios.get(`${API_BASE}/sensors`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSensors(res.data);
    } catch (err) {
      console.error('Fetch failed', err);
    }
  };

  const fetchSensorLimit = async () => {
    try {
      const res = await axios.get(`${API_BASE}/sensor/quota`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAllowedSensors(res.data.allowed);
    } catch (err) {
      console.error('Failed to fetch allowed sensors', err);
    }
  };

  const handleCreate = async () => {
  if (!form.name.trim()) return;
  try {
    const res = await axios.post(`${API_BASE}/admin/sensor`, form, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setApiMessage(res.data.message);
    if (res.data.message.includes("success")){setApiSuccess(true);}
    else{setApiSuccess(false);}
    setForm({ name: '', description: '' });
    setShowCreateForm(false);
    fetchSensors();
  } catch (err) {
    console.error('Create failed', err);
    setApiMessage('Failed to create sensor');
  }
};

  const handleEdit = async () => {
    try {
      await axios.put(`${API_BASE}/admin/sensor/${editing.id}`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEditing(null);
      setForm({ name: '', description: '' });
      fetchSensors();
    } catch (err) {
      console.error('Edit failed', err);
    }
  };

  const handleDisable = async (id) => {
    if (!window.confirm('Disable this sensor?')) return;
    try {
      await axios.get(`${API_BASE}/admin/sensor/disable/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSensors();
    } catch (err) {
      console.error('Disable failed', err);
    }
  };

  const handleEnable = async (id) => {
    if (!window.confirm('Enable this sensor?')) return;
    try {
      await axios.get(`${API_BASE}/admin/sensor/enable/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSensors();
    } catch (err) {
      console.error('Enable failed', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this sensor?')) return;
    try {
      await axios.delete(`${API_BASE}/admin/sensor/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchSensors();
    } catch (err) {
      console.error('Delete failed', err);
    }
  };

  const toggleSort = (key) => {
    if (key === sortKey) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const filteredSensors = sensors
    .filter((d) =>`${d.name} ${d.description}`.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter((d) => !statusFilter || d.status === statusFilter)
    .sort((a, b) => {
      const aVal = a[sortKey]?.toString().toLowerCase() || '';
      const bVal = b[sortKey]?.toString().toLowerCase() || '';
      return sortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });

  const statusCounts = sensors.reduce((acc, d) => {
    acc[d.status] = (acc[d.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full space-y-10 text-[var(--text-primary)] font-[var(--font-ui)]">
      {/* Header */}
      <div className="space-y-1 border-b border-[var(--surface-800)] pb-4">
        <h1 className="text-2xl font-bold tracking-wide">Sensor Management</h1>
        <p className="text-sm text-[var(--text-muted)]">Create and manage wireless sensors</p>
      </div>

      {/* Top Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
        {(() => {
          const usage = sensors.length;
          const percent = (usage / allowedSensors) * 100;
          let barColor = 'bg-[var(--status-success)]';
          if (percent >= 90) barColor = 'bg-[var(--status-error)]';
          else if (percent >= 70) barColor = 'bg-[var(--status-warn)]';

          return (
            <div className="p-4 bg-[var(--surface-900)] rounded-xl border border-[var(--surface-800)]">
              <div className="flex items-center gap-3">
                <Gauge className="w-6 h-6 text-[var(--text-muted)]" />
                <span className="text-lg font-semibold text-[var(--text-muted)]">Sensor Quota</span>
              </div>
              <p className="mt-2 text-3xl font-bold text-[var(--text-primary)]">
                {usage}
                <span className="text-base text-[var(--text-muted)]"> / {allowedSensors || 0}</span>
              </p>
              <div className="mt-2 w-full h-2 bg-[var(--surface-800)] rounded-full overflow-hidden">
                <div className={`h-full ${barColor} transition-all duration-300`} style={{ width: `${percent}%` }} />
              </div>
            </div>
          );
        })()}
        {Object.entries(statusConfig).map(([key, cfg]) => (
          <div
            key={key}
            onClick={() => setStatusFilter(key === statusFilter ? '' : key)}
            className={`cursor-pointer p-4 rounded-xl border transition
              ${statusFilter === key ? 'bg-[var(--surface-800)] border-[var(--signal-cyan)]' : 'bg-[var(--surface-900)] border-[var(--surface-800)]'}`}
          >
            <div className="flex items-center gap-3">
              <cfg.icon className={`w-6 h-6 ${cfg.color}`} />
              <span className={`text-lg font-semibold ${cfg.color}`}>{cfg.label}</span>
            </div>
            <p className="mt-2 text-3xl font-bold">{statusCounts[key] || 0}</p>
          </div>
        ))}
      </div>

      {/* Create Button */}
      {role === 'admin' && (
        <div>
         <div className="flex items-center gap-4">
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center gap-2 text-[var(--signal-cyan)] border border-[var(--signal-cyan)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
            >
              {showCreateForm ? <MinusCircleIcon className="w-5 h-5" /> : <PlusCircleIcon className="w-5 h-5" />}
              {showCreateForm ? 'Hide Create Form' : 'Create Sensor'}
            </button>
            {apiMessage && (
              <span className={`flex items-center gap-2 px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition transition-all duration-300 ${
                apiSuccess ? 'text-[var(--status-success)]' : 'text-[var(--status-error)]'
              }`}>
                {apiMessage}
              </span>
            )}
         </div>

          {showCreateForm && (
            <div className="mt-4 bg-[var(--surface-900)] border border-[var(--surface-800)] p-6 rounded-xl">
              <h2 className="text-xl font-semibold mb-4">Create Sensor</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  className="px-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
                  placeholder="Sensor name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <input
                  className="px-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
                  placeholder="Description (optional)"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              <button
                onClick={handleCreate}
                className="mt-4 text-[var(--status-success)] border border-[var(--status-success)] px-4 py-2 rounded-md hover:bg-[var(--surface-800)] transition"
              >
                Submit
              </button>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-2.5 w-5 h-5 text-[var(--text-muted)]" />
          <input
            className="w-full pl-10 pr-4 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md"
            placeholder="Search sensors..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="relative">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full appearance-none pl-4 pr-9 py-2 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded-md text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--signal-cyan)] focus:border-[var(--signal-cyan)] transition-colors"
          >
            <option value="">All Statuses</option>
            <option value="enabled">Enabled</option>
            <option value="pending">Pending</option>
            <option value="disabled">Disabled</option>
          </select>
          <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        </div>
      </div>

      {/* Sortable Table */}
      <div className="overflow-x-auto bg-[var(--surface-900)] border border-[var(--surface-800)] rounded-xl">
        <table className="min-w-full text-sm text-[var(--text-primary)]">
  <thead className="bg-[var(--surface-800)] text-[var(--text-muted)]">
    <tr>
      <th
      className="px-4 py-2 text-left cursor-pointer select-none"
      onClick={() => toggleSort('name')}
    >
      Name {sortKey === 'name' && (sortAsc ? '▲' : '▼')}
    </th>
      <th
      className="px-4 py-2 text-left cursor-pointer select-none"
      onClick={() => toggleSort('description')}
    >
      Description {sortKey === 'description' && (sortAsc ? '▲' : '▼')}
    </th>
      <th className="px-4 py-2 text-left">Status</th>
      <th className="px-4 py-2 text-right">Actions</th>
    </tr>
  </thead>
  <tbody>
    {filteredSensors.map((sensor) => (
      <tr
        key={sensor.id}
        className="border-b border-[var(--surface-800)] hover:bg-[var(--surface-800)]"
      >
        {editing?.id === sensor.id ? (
          <>
            <td className="px-4 py-2">
              <input
                className="px-2 py-1 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded w-full"
                value={form.name}
                onChange={(e) =>
                  setForm({ ...form, name: e.target.value })
                }
              />
            </td>
            <td className="px-4 py-2">
              <input
                className="px-2 py-1 bg-[var(--ink-950)] border border-[var(--surface-800)] rounded w-full"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </td>
            <td className="px-4 py-2">
              <StatusBadge status={sensor.status} />
            </td>
            <td className="px-4 py-2 text-right space-x-2">
              <button
                onClick={handleEdit}
                className="text-[var(--status-success)] border border-[var(--status-success)] px-2 py-1 rounded hover:bg-[var(--surface-800)]"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setEditing(null);
                  setForm({ name: '', description: '' });
                }}
                className="text-[var(--text-muted)] border border-[var(--surface-800)] px-2 py-1 rounded hover:bg-[var(--surface-800)]"
              >
                Cancel
              </button>
            </td>
          </>
        ) : (
          <>
            <td className="px-4 py-2 font-medium">{sensor.name}</td>
            <td className="px-4 py-2 text-[var(--text-muted)]">
              {sensor.description}
            </td>
            <td className="px-4 py-2">
              <StatusBadge status={sensor.status} />
            </td>
              <td className="px-4 py-2 text-right space-x-2">
                {sensor.status === "pending" && (
                  <SensorCommandPopup sensor={sensor} token={token} />
                )}
                {sensor.status === "enabled" && (
                <button
                  onClick={() => handleDisable(sensor.id)}
                  aria-label="Disable sensor"
                  title="Disable sensor"
                  className="text-[var(--status-error)] hover:text-[var(--text-primary)]"
                >
                  <SignalSlashIcon className="w-5 h-5 inline" />
                </button>
              )}
              {sensor.status === "disabled" && (
              <button
                onClick={() => handleEnable(sensor.id)}
                aria-label="Enable sensor"
                title="Enable sensor"
                className="text-[var(--status-success)] hover:text-[var(--text-primary)]"
              >
                <SignalIcon className="w-5 h-5 inline" />
              </button>
            )}
              <button
                onClick={() => {
                  setEditing(sensor);
                  setForm({
                    name: sensor.name || '',
                    description: sensor.description || '',
                  });
                }}
                aria-label="Edit sensor"
                title="Edit sensor"
                className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              >
                <PencilSquareIcon className="w-5 h-5 inline" />
              </button>
              <button
                onClick={() => handleDelete(sensor.id)}
                aria-label="Delete sensor"
                title="Delete sensor"
                className="text-[var(--status-error)] hover:text-[var(--text-primary)]"
              >
                <TrashIcon className="w-5 h-5 inline" />
                </button>
            </td>
          </>
        )}
      </tr>
    ))}

    {filteredSensors.length === 0 && (
      <tr>
        <td colSpan="4" className="text-center py-6 text-[var(--text-muted)]">
          No matching sensors found.
        </td>
      </tr>
    )}
  </tbody>
</table>

      </div>
    </div>
  );
}
